import { OBaseModel } from '@olameter/o-utilities';
import { FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor as ProtoCompositeFilterDescriptor } from 'src/app/proto/generated/inv_common_message_pb';

const separatorPipe = '||';

export class ColumnSettings implements OBaseModel {
  name: string;
  field: string;
  title: string;
  filter?: 'text' | 'numeric' | 'boolean' | 'date';
  format?: string;
  width?: number;
  filterable?: boolean;
  hidden?: boolean = true;
  isJson?: boolean;
  path?: string;

  static getFiltersText(
    filterValue: FilterDescriptor[],
    columnsConfig: ColumnSettings[],
    selectedWOs = []
  ): string {
    const [_members, _operators, _values] = this.getFiltersMultipleText(
      filterValue,
      columnsConfig
    );
    const woStatuses = `${selectedWOs?.map((x) => x).join(separatorPipe)}`;
    return `_members=${_members}&_operators=${_operators}&_values=${_values}&_wo_status_names=${woStatuses}`;
  }

  static getFiltersArrays(
    filterValue: FilterDescriptor[],
    columnsConfig: ColumnSettings[]
  ): { member: string; operator: string; value: string }[] {
    const [_members, _operators, _values] = this.getFiltersMultipleText(
      filterValue,
      columnsConfig
    );

    const members = _members.split(separatorPipe);
    const operators = _operators.split(separatorPipe);
    const values = _values.split(separatorPipe);

    const accumulate = members.reduce((acc, current, index) => {
      return [
        ...acc,
        { member: current, operator: operators[index], value: values[index] },
      ];
    }, []);

    return accumulate;
  }

  static getFiltersProto(
    filterValue: FilterDescriptor[],
    columnsConfig: ColumnSettings[]
  ): ProtoCompositeFilterDescriptor {
    const filters = this.getFiltersArrays(filterValue, columnsConfig);
    let previousMember = '';

    const accumulate = filters.reduce((acc, current) => {
      const p: ProtoCompositeFilterDescriptor =
        new ProtoCompositeFilterDescriptor();
      p.setField(current.member);
      p.setOperator(current.operator);
      p.setValue(current.value);
      p.setLogic(this.getLogic(previousMember, current.member, current.operator));
      previousMember = current.member;
      return [...acc, p];
    }, []);

    const ret: ProtoCompositeFilterDescriptor =
      new ProtoCompositeFilterDescriptor();
    ret.setFiltersList(accumulate);
    return ret;
  }

  static getLogic(previousMember, member, operator) {
    let logic = 'AND';

    if (previousMember === member) {
      switch (operator) {
        case FilterOperator.EqualTo:
        case FilterOperator.StartsWith:
        case FilterOperator.EndsWith:
        case FilterOperator.Contains:
          logic = 'OR';
          break;
      }
    }

    return logic;
  }

  static getFiltersMultipleText(
    filterValue: FilterDescriptor[],
    columnsConfig: ColumnSettings[]
  ) {
    let _members = '';
    let _operators = '';
    let _values = '';
    let columnCgf: ColumnSettings = null;
    let _currentmember = '';
    let _currentoperator = '';
    const filterLengh = filterValue?.length - 1;
    const fFinal = (index: number) =>
      index === filterLengh ? '' : separatorPipe;

    filterValue?.forEach((x: FilterDescriptor, index) => {
      columnCgf = columnsConfig?.filter((item) => item != null).find(
        (y: ColumnSettings) => y.field === x.field
      );

      if (columnCgf?.isJson) {
        _currentmember = this.getColumnPathTextForDb(columnCgf.path, x.field);
      } else {
        _currentmember = ((`"` + x.field) as string) + `"`;
      }

      _currentoperator = x.operator as string;

      if (Array.isArray(x.value)) {
        const members =
          x.value.length > 0
            ? x.value.map(() => _currentmember)
            : [_currentmember];
        const operators =
          x.value.length > 0
            ? x.value.map(() => _currentoperator)
            : [_currentoperator];
        _members += members.join(separatorPipe) + fFinal(index);
        _operators += operators.join(separatorPipe) + fFinal(index);

        const valueFormated = x.value.map((xV) =>
          this.FormatValueToSupportOnDB(xV, columnCgf)
        );
        _values += valueFormated.join(separatorPipe) + fFinal(index);
      } else {
        _members += _currentmember + fFinal(index);
        _operators += _currentoperator + fFinal(index);
        _values +=
          this.FormatValueToSupportOnDB(x.value, columnCgf) + fFinal(index);
      }
    });

    if (_members == '"undefined"') _members = '';
    if (_operators == 'undefined') _operators = '';
    if (_values == 'undefined') _values = '';

    return [_members, _operators, _values] as const;
  }

  private static getColumnPathTextForDb(
    path: string,
    field?: string | Function
  ): string {
    const str = path.split('>');
    const retVal = `"${str[0]}"->'${str[1]}'->>'${field}'`;
    return retVal;
  }

  private static FormatValueToSupportOnDB(
    value: any,
    columnCfg: ColumnSettings
  ): string | any {
    let decodedVal = value;
    while (decodedVal !== decodeURIComponent(decodedVal)) {
      decodedVal = decodeURIComponent(decodedVal);
    }
    return decodedVal;
  }
}
